// @ts-nocheck - MUI 7.x Grid item prop type compatibility
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  IconButton,
  Paper,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  InputAdornment,
} from '@mui/material';
import {
  Send,
  SmartToy,
  Person,
  TrendingUp,
  TrendingDown,
  Search,
  Business,
  Description,
} from '@mui/icons-material';
import api from '../services/api';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  type?: string;
  data?: any;
  suggestions?: string[];
  timestamp: Date;
}

interface SearchResult {
  customer_id?: string;
  contract_id?: string;
  contract_number?: string;
  name?: string;
  client_name?: string;
  active_contracts?: number;
  equipment_count?: number;
}

export default function ProfitabilityChat() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState<'clients' | 'contracts'>('clients');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Add initial help message
  useEffect(() => {
    setMessages([{
      role: 'assistant',
      content: `Hola! Soy tu asistente de rentabilidad CDP. Puedo ayudarte a:

- **Buscar clientes** por nombre o ID
- **Buscar contratos** por numero
- **Analizar rentabilidad** de clientes y contratos
- **Identificar contratos** de bajo margen
- **Comparar clientes**

Escribe tu pregunta o usa la busqueda rapida a la derecha.`,
      type: 'help',
      timestamp: new Date()
    }]);
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Search for clients or contracts
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setSearching(true);
    try {
      const response = await api.post('/agent/search', {
        query: searchQuery,
        entity_type: searchType,
        limit: 10
      });
      setSearchResults(response.data.results);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  // Send message to agent
  const sendMessage = async (messageText?: string) => {
    const text = messageText || input.trim();
    if (!text) return;

    // Add user message
    const userMessage: ChatMessage = {
      role: 'user',
      content: text,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await api.post('/agent/chat', {
        message: text
      });

      // Add assistant response
      const assistantMessage: ChatMessage = {
        role: 'assistant',
        content: response.data.message,
        type: response.data.type,
        data: response.data.data,
        suggestions: response.data.suggestions,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error: any) {
      const errorMessage: ChatMessage = {
        role: 'assistant',
        content: `Error: ${error.response?.data?.detail || 'No se pudo procesar la solicitud'}`,
        type: 'error',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  // Handle search result click
  const handleResultClick = (result: SearchResult) => {
    if (result.customer_id) {
      sendMessage(`rentabilidad cliente ${result.customer_id}`);
    } else if (result.contract_number) {
      sendMessage(`rentabilidad contrato ${result.contract_number}`);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: string) => {
    sendMessage(suggestion);
  };

  // Format currency
  const formatCurrency = (amount: number | string): string => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('es-PA', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(num || 0);
  };

  // Render data based on type
  const renderData = (message: ChatMessage) => {
    if (!message.data) return null;

    const { type, data } = message;

    // Search results
    if (type === 'search_results' && Array.isArray(data)) {
      return (
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {data[0]?.customer_id && (
                  <>
                    <TableCell>ID</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Contratos</TableCell>
                  </>
                )}
                {data[0]?.contract_number && (
                  <>
                    <TableCell>Contrato</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Equipos</TableCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((item: any, idx: number) => (
                <TableRow
                  key={idx}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleResultClick(item)}
                >
                  {item.customer_id && (
                    <>
                      <TableCell>{item.customer_id}</TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.active_contracts}</TableCell>
                    </>
                  )}
                  {item.contract_number && (
                    <>
                      <TableCell>{item.contract_number}</TableCell>
                      <TableCell>{item.client_name}</TableCell>
                      <TableCell>{item.equipment_count}</TableCell>
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    // Profitability data
    if (type === 'profitability' && typeof data === 'object') {
      const isClient = 'contracts_count' in data;

      return (
        <Card variant="outlined" sx={{ mt: 1 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {isClient ? data.client_name : `Contrato ${data.contract_number}`}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Ingresos</Typography>
                <Typography variant="body1" fontWeight="bold">
                  {formatCurrency(data.total_revenue || data.revenue?.total)}
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Costo Real</Typography>
                <Typography variant="body1">
                  {formatCurrency(data.total_real_cost || data.real_costs?.total)}
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Ganancia Real</Typography>
                <Typography
                  variant="body1"
                  fontWeight="bold"
                  color={(data.total_real_profit || data.real_profit) >= 0 ? 'success.main' : 'error.main'}
                >
                  {formatCurrency(data.total_real_profit || data.real_profit)}
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">Margen</Typography>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="body1" fontWeight="bold">
                    {(data.avg_real_margin_pct || data.real_margin_pct || 0).toFixed(1)}%
                  </Typography>
                  {(data.avg_real_margin_pct || data.real_margin_pct || 0) >= 20 ? (
                    <TrendingUp color="success" fontSize="small" />
                  ) : (
                    <TrendingDown color="error" fontSize="small" />
                  )}
                </Box>
              </Grid>
            </Grid>

            {data.analysis && (
              <Alert severity="info" sx={{ mt: 2 }}>
                {data.analysis}
              </Alert>
            )}

            {isClient && (
              <Box mt={2}>
                <Typography variant="caption" color="textSecondary">
                  {data.contracts_count} contratos | {data.equipment_count} equipos | Periodo: {data.period}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      );
    }

    // Ranking/Top clients
    if (type === 'ranking' && Array.isArray(data)) {
      return (
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell align="right">Ingresos</TableCell>
                <TableCell align="right">Ganancia</TableCell>
                <TableCell align="right">Margen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((client: any, idx: number) => (
                <TableRow
                  key={idx}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => sendMessage(`rentabilidad cliente ${client.client_id}`)}
                >
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{client.client_name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {client.client_id}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{formatCurrency(client.revenue)}</TableCell>
                  <TableCell align="right">
                    <Typography color={client.profit >= 0 ? 'success.main' : 'error.main'}>
                      {formatCurrency(client.profit)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${client.margin_pct?.toFixed(1)}%`}
                      size="small"
                      color={client.margin_pct >= 20 ? 'success' : client.margin_pct >= 10 ? 'warning' : 'error'}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    // Alerts/Low margin contracts
    if (type === 'alerts' && Array.isArray(data)) {
      return (
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Contrato</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell align="right">Margen</TableCell>
                <TableCell>Recomendacion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((contract: any, idx: number) => (
                <TableRow
                  key={idx}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => sendMessage(`rentabilidad contrato ${contract.contract_number}`)}
                >
                  <TableCell>{contract.contract_number}</TableCell>
                  <TableCell>{contract.client_name}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={`${contract.margin_pct?.toFixed(1)}%`}
                      size="small"
                      color={contract.margin_pct < 0 ? 'error' : 'warning'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{contract.recommendation}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      );
    }

    // Error data
    if (data.error) {
      return (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {data.error}
        </Alert>
      );
    }

    return null;
  };

  return (
    <Box sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h4" gutterBottom>
        Asistente de Rentabilidad
      </Typography>

      <Grid container spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        {/* Chat Area */}
        <Grid item xs={12} md={8}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Messages */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              {messages.map((message, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                    mb: 2
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: '85%',
                      display: 'flex',
                      gap: 1,
                      flexDirection: message.role === 'user' ? 'row-reverse' : 'row'
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: message.role === 'user' ? 'primary.main' : 'grey.300',
                        color: message.role === 'user' ? 'white' : 'text.primary',
                        flexShrink: 0
                      }}
                    >
                      {message.role === 'user' ? <Person fontSize="small" /> : <SmartToy fontSize="small" />}
                    </Box>
                    <Box>
                      <Paper
                        sx={{
                          p: 1.5,
                          bgcolor: message.role === 'user' ? 'primary.main' : 'grey.100',
                          color: message.role === 'user' ? 'white' : 'text.primary'
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ whiteSpace: 'pre-wrap' }}
                          dangerouslySetInnerHTML={{
                            __html: message.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                          }}
                        />
                      </Paper>

                      {/* Render data */}
                      {renderData(message)}

                      {/* Suggestions */}
                      {message.suggestions && message.suggestions.length > 0 && (
                        <Box mt={1} display="flex" gap={1} flexWrap="wrap">
                          {message.suggestions.map((suggestion, sIdx) => (
                            <Chip
                              key={sIdx}
                              label={suggestion}
                              size="small"
                              variant="outlined"
                              onClick={() => handleSuggestionClick(suggestion)}
                              sx={{ cursor: 'pointer' }}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              ))}

              {loading && (
                <Box display="flex" alignItems="center" gap={1} ml={5}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="textSecondary">
                    Analizando...
                  </Typography>
                </Box>
              )}

              <div ref={messagesEndRef} />
            </Box>

            {/* Input */}
            <Divider />
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                placeholder="Escribe tu pregunta... (ej: rentabilidad cliente A0032)"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => sendMessage()}
                        disabled={loading || !input.trim()}
                        color="primary"
                      >
                        <Send />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Box>
          </Card>
        </Grid>

        {/* Quick Search Panel */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1, overflow: 'auto' }}>
              <Typography variant="h6" gutterBottom>
                Busqueda Rapida
              </Typography>

              {/* Search type toggle */}
              <Box display="flex" gap={1} mb={2}>
                <Chip
                  icon={<Business />}
                  label="Clientes"
                  onClick={() => setSearchType('clients')}
                  color={searchType === 'clients' ? 'primary' : 'default'}
                  variant={searchType === 'clients' ? 'filled' : 'outlined'}
                />
                <Chip
                  icon={<Description />}
                  label="Contratos"
                  onClick={() => setSearchType('contracts')}
                  color={searchType === 'contracts' ? 'primary' : 'default'}
                  variant={searchType === 'contracts' ? 'filled' : 'outlined'}
                />
              </Box>

              {/* Search input */}
              <TextField
                fullWidth
                size="small"
                placeholder={searchType === 'clients' ? 'Nombre o ID del cliente...' : 'Numero de contrato...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleSearch} disabled={searching}>
                        {searching ? <CircularProgress size={20} /> : <Search />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 2 }}
              />

              {/* Search results */}
              {searchResults.length > 0 && (
                <List dense>
                  {searchResults.map((result, idx) => (
                    <ListItem key={idx} disablePadding>
                      <ListItemButton onClick={() => handleResultClick(result)}>
                        <ListItemText
                          primary={result.name || result.client_name || result.contract_number}
                          secondary={
                            result.customer_id
                              ? `${result.customer_id} - ${result.active_contracts} contratos`
                              : `${result.client_name} - ${result.equipment_count} equipos`
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Quick actions */}
              <Typography variant="subtitle2" gutterBottom>
                Acciones Rapidas
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                <Chip
                  label="Top 10 Clientes"
                  onClick={() => sendMessage('mejores clientes')}
                  variant="outlined"
                  sx={{ justifyContent: 'flex-start' }}
                />
                <Chip
                  label="Contratos Bajo Margen"
                  onClick={() => sendMessage('contratos bajo margen')}
                  variant="outlined"
                  color="warning"
                  sx={{ justifyContent: 'flex-start' }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
